import type { TaskInputPrompts } from '@/app/types';
import { getAspectRatio } from '@/app/utils';
import type { TaskImageItem } from '../../types';

export const Base64Util = {
  encode: (input: string): string => {
    if (typeof window !== 'undefined' && window.btoa) {
      return window.btoa(unescape(encodeURIComponent(input)));
    }
    return Buffer.from(input, 'utf-8').toString('base64');
  },

  decode: (base64: string): string => {
    if (typeof window !== 'undefined' && window.atob) {
      return decodeURIComponent(escape(window.atob(base64)));
    }
    return Buffer.from(base64, 'base64').toString('utf-8');
  },
};

export const getQueryParams = (url: string) => {
  const queryString = url.split('?')[1] || '';
  const params = new URLSearchParams(queryString);
  const result: Record<string, string> = {};

  Array.from(params.entries()).forEach(([key, value]) => {
    result[key] = value;
  });

  return result;
};

/**
 * 判断是否是Google Chrome
 * @returns
 */
export function isChromeEdgeOpera() {
  const ua = navigator.userAgent;
  const isChromium = window.chrome !== undefined;
  const vendor = navigator.vendor;

  const isGoogleChrome = isChromium && vendor === 'Google Inc.' && !ua.includes('Edg') && !ua.includes('OPR');
  const isEdge = ua.includes('Edg');
  const isOpera = ua.includes('OPR');

  return isGoogleChrome || isEdge || isOpera;
}

export const getVideoPlayerStyle = (videoSize: { width?: number; height?: number } | undefined) => {
  if (videoSize?.width && videoSize?.height) {
    // 计算宽高比
    const aspectRatio = getAspectRatio({ size: videoSize });
    // 基于屏幕宽度和宽高比计算高度，假设容器宽度为屏幕宽度
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
    const calculatedHeight = screenWidth / aspectRatio;
    // 限制最小高度，最大高度由外层容器的max-h-[457px]控制
    const height = Math.max(200, Math.min(calculatedHeight, 457));

    return { height: `${height}px` };
  }
  return {};
};

/**
 * 获取任务中的图片信息（用于获取上传的图片）
 */
export function getTaskImages(taskPrompts?: Array<TaskInputPrompts>): TaskImageItem[] {
  if (!taskPrompts) return [];
  const images: TaskImageItem[] = [];
  taskPrompts
    .filter((prompt) => prompt.type === 'image' || prompt.type === 'material')
    // biome-ignore lint/suspicious/useIterableCallbackReturn: this map is used for side effects to collect image items
    .map((prompt) => {
      const { type, name = '', material, content } = prompt;
      // 主体
      if (type === 'material') {
        // 未公开主体，只有名字
        if (!material?.id || material.id === '0') {
          images.push({
            url: '',
            name,
          });
        }
        // 公开主体
        images.push(
          ...(material?.contents?.map((content) => ({
            url: content,
            name,
          })) || []),
        );
      } else if (type === 'image') {
        // 图片
        images.push({
          url: content || '',
          name: '',
        });
      }
    });

  // 过滤掉没有图片和名字都没有的图片
  return images.filter((item) => item.url || item.name);
}

/**
 * 获取任务中的文本提示词
 */
export function getTaskTextPrompt(taskPrompts?: Array<TaskInputPrompts>): string {
  if (!taskPrompts) return '';

  const textPrompt = taskPrompts
    .filter((prompt) => prompt.type === 'text')
    .map((prompt) => prompt.content)
    .find(Boolean);

  return textPrompt || '';
}
